import {
  browserLocalPersistence,
  confirmPasswordReset,
  inMemoryPersistence,
  onAuthStateChanged as onFirebaseAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
} from 'firebase/auth';
import i18next from 'i18next';

import { toast } from '../../components';
import { auth } from './firebase.config.ts';

export const verifyPasswordResetCode = (
  code: string,
): ReturnType<typeof firebaseVerifyPasswordResetCode> => {
  return firebaseVerifyPasswordResetCode(auth, code);
};

export const resetPassword = (
  code: string,
  password: string,
): ReturnType<typeof confirmPasswordReset> => {
  return confirmPasswordReset(auth, code, password);
};

export const sendResetPasswordEmail = (
  email: string,
): ReturnType<typeof sendPasswordResetEmail> => {
  return sendPasswordResetEmail(auth, email);
};

export const signIn = async (
  email: string,
  password: string,
  rememberMe: boolean,
): ReturnType<typeof signInWithEmailAndPassword> => {
  if (rememberMe) {
    await setPersistence(auth, browserLocalPersistence);
  } else {
    await setPersistence(auth, inMemoryPersistence);
  }

  setAuthExpiry();

  document.addEventListener('click', setSessionInactivityTimeout);

  return signInWithEmailAndPassword(auth, email, password);
};

export const signOut = (): ReturnType<typeof firebaseSignOut> => {
  document.removeEventListener('click', setSessionInactivityTimeout);

  return firebaseSignOut(auth);
};

export const onAuthStateChanged = (
  callback: (user: User | null) => void,
): ReturnType<typeof onFirebaseAuthStateChanged> => {
  return onFirebaseAuthStateChanged(auth, callback);
};

const AUTH_EXPIRY_KEY = 'AUTH_EXPIRY';
export const AUTO_LOGOUT_AFTER_TIME_DAYS = 2;
const AUTO_LOGOUT_AFTER_TIME_MS =
  AUTO_LOGOUT_AFTER_TIME_DAYS * 24 * 60 * 60 * 1000;

export const setAuthExpiry = () => {
  const expiryTime = Date.now().valueOf() + AUTO_LOGOUT_AFTER_TIME_MS;
  localStorage.setItem(AUTH_EXPIRY_KEY, expiryTime.toString());
};

export const checkAuthExpiry = async (): Promise<boolean> => {
  const expiryTimeStr = localStorage.getItem(AUTH_EXPIRY_KEY);

  if (expiryTimeStr === null) {
    return true;
  }

  const now = Date.now().valueOf();
  const expiryTime = parseInt(expiryTimeStr, 10);

  if (now >= expiryTime) {
    await signOut();
    localStorage.removeItem(AUTH_EXPIRY_KEY);
    return false;
  }

  return true;
};

const SESSION_INACTIVITY_DURATION_MS = 15 * 60 * 1000;

let SESSION_TIMEOUT: NodeJS.Timeout | null = null;

export const setSessionInactivityTimeout = () => {
  if (SESSION_TIMEOUT !== null) {
    clearTimeout(SESSION_TIMEOUT);
  }

  SESSION_TIMEOUT = setTimeout(() => {
    void signOut().then(() =>
      toast.error(
        i18next.t('errors.inactivityLogout.title'),
        i18next.t('errors.inactivityLogout.message'),
      ),
    );
  }, SESSION_INACTIVITY_DURATION_MS);
};
